/**
 * MAPS — the map registry.
 *
 * Every `src/world/maps/*.js` file that exports a `MAP` spec is a playable map,
 * discovered automatically. Adding one is a one-file change: drop it in, and it
 * appears in the vote screen and in `?map=<id>`. Nothing shared needs editing,
 * which is the point — several authors can add maps at once without touching the
 * same lines.
 *
 * See `./CONTRACT.md` for the spec every map file must satisfy.
 *
 * PUBLIC API
 *   listMaps()            all specs, in vote-screen order
 *   getMap(id)            one spec, or null
 *   resolveMapId()        what the player asked for: ?map=, then the stored
 *                         vote, then DEFAULT_MAP
 *   rememberMapId(id)     persist a chosen map across the reload that loads it
 *   applyMapPalette(spec) merge the map's own palette keys into PALETTE
 */

import { PALETTE } from '../palette.js';
import { MAP as MARKET } from './market.js';

export const DEFAULT_MAP = 'market';

/** Where a vote is parked so it survives the reload that builds the map. */
const STORE_KEY = 'rift:map';

/**
 * Vite resolves this into one dynamic import per sibling map, so the registry
 * needs no hand-maintained list. Outside Vite (a plain `node` probe) the call
 * does not exist and the registry holds `market` alone, which is the only map
 * those probes have ever needed.
 *
 * LAZY, NOT EAGER — deliberately. An eager glob compiles to static imports, so a
 * single map file with a syntax error takes down the whole module graph: the
 * registry, the world, the menu, the game. Loading each map through its own
 * dynamic import makes a broken map a *skipped* map. That matters most exactly
 * when it hurts most — several authors adding maps at once, where one half-saved
 * file would otherwise black-screen everyone else's work.
 */
const LOADERS = (() => {
  try {
    return import.meta.glob('./*.js');
  } catch {
    return null;
  }
})();

const REGISTRY = new Map();
/** Market is imported statically: there must always be a map to fall back to. */
REGISTRY.set(MARKET.id, MARKET);

/** Names of maps that exist on disk but failed to load. Surfaced, never silent. */
export const brokenMaps = [];

if (LOADERS) {
  await Promise.all(
    Object.entries(LOADERS).map(async ([path, load]) => {
      if (path.endsWith('/index.js') || path.endsWith('/market.js')) return;
      try {
        const mod = await load();
        const spec = mod?.MAP ?? mod?.default;
        // A file under maps/ that is not a map (a shared helper, say) is skipped
        // rather than crashing the registry — but never silently: a map whose
        // author has just mistyped its export looks exactly like a map that
        // vanished, and that is a miserable thing to debug.
        //
        // An id is the whole requirement. `BUILDINGS` is NOT: a map that raises
        // its own geometry in `decorate` (a station, a ship, a cave) legitimately
        // has no entries in the shared building kit, and `useMap` defaults it.
        if (typeof spec?.id !== 'string' || !spec.id) {
          brokenMaps.push(path);
          console.warn(`[maps] "${path}" exports no MAP spec with an id — skipped`);
          return;
        }
        REGISTRY.set(spec.id, spec);
      } catch (err) {
        brokenMaps.push(path);
        console.error(`[maps] "${path}" failed to load and was skipped —`, err?.message ?? err);
      }
    })
  );
}

/** All maps, ordered by their own `order` field then by name. */
export function listMaps() {
  return [...REGISTRY.values()].sort(
    (a, b) => (a.order ?? 100) - (b.order ?? 100) || String(a.name).localeCompare(String(b.name))
  );
}

export function getMap(id) {
  return REGISTRY.get(id) ?? null;
}

export function hasMap(id) {
  return REGISTRY.has(id);
}

/**
 * Which map to build. `?map=` wins so the capture harness and a shared link can
 * both pin one; otherwise the last vote; otherwise Market.
 *
 * A capture run with no explicit map MUST get the default, or the pixel gate in
 * `tools/imagediff.mjs` would compare a stored vote against its baseline.
 */
export function resolveMapId(search = typeof location === 'undefined' ? '' : location.search) {
  const params = new URLSearchParams(search);
  const asked = params.get('map');
  if (asked && hasMap(asked)) return asked;
  if (params.get('capture') === '1') return DEFAULT_MAP;
  try {
    const stored = globalThis.localStorage?.getItem(STORE_KEY);
    if (stored && hasMap(stored)) return stored;
  } catch {
    // Storage can be denied outright; the default is always available.
  }
  return DEFAULT_MAP;
}

export function rememberMapId(id) {
  try {
    globalThis.localStorage?.setItem(STORE_KEY, id);
  } catch {
    /* not fatal — the URL carries the choice too */
  }
}

/**
 * A map may declare surface keys of its own (`spec.palette`) instead of editing
 * the shared `palette.js`, so two map authors never collide in that file. Keys
 * are merged in before anything builds and removed again on a map change, so a
 * key one map defines can never leak into another.
 */
let injected = [];
export function applyMapPalette(spec) {
  for (const k of injected) delete PALETTE[k];
  injected = [];
  const extra = spec?.palette;
  if (!extra) return;
  for (const [k, def] of Object.entries(extra)) {
    if (PALETTE[k]) {
      // Shadowing a shared key would change how OTHER maps look the moment this
      // one is loaded in the same session. Namespace collisions are the author's
      // to fix, so say so loudly rather than silently winning.
      console.warn(`[maps] "${spec.id}" redefines shared palette key "${k}" — ignored`);
      continue;
    }
    PALETTE[k] = def;
    injected.push(k);
  }
}
